/**
 * @file dialog-handler.js
 * @description Componente unificado para dialogs (CRA)
 */

import React from "react"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../../ui/dialog.js"
import { Button } from "../../../ui/button.js"
import { useIsMobile } from "../../../../hooks/use-mobile.js"
import { cn } from "../../../../lib/utils.js"
import { getDialogColors, getDialogIcon } from "../utils/dialog-utils.js"
import { DialogMobile } from "./dialog-mobile.js"

/**
 * Componente unificado para dialogs
 * @param {Object} props - Propriedades do componente
 * @returns {React.ReactElement} Componente de dialog
 */
export function DialogHandler({
  open,
  onClose,
  title = "Informação",
  message = "",
  type = "info",
  confirmText = "OK",
  cancelText = "Cancelar",
  onConfirm,
  onCancel,
  showCancel = false,
  className,
}) {
  const isMobile = useIsMobile()
  const colors = getDialogColors(type)
  const icon = getDialogIcon(type)

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm()
    }
    onClose()
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    }
    onClose()
  }

  // Se for mobile, usar o drawer
  if (isMobile) {
    return React.createElement(DialogMobile, {
      open,
      onClose,
      title,
      message,
      type,
      confirmText,
      cancelText,
      onConfirm,
      onCancel,
      showCancel,
      className,
    })
  }

  // Se for desktop, usar o dialog normal
  return React.createElement(
    Dialog,
    { open, onOpenChange: onClose },
    React.createElement(
      DialogContent,
      { 
        className: cn("sm:max-w-md z-[1701]", className),
        overlayClassName: "z-[1700]",
        style: {
          backgroundColor: "#ffffff",
          color: "#1f2937",
        }
      },
      React.createElement(
        DialogHeader,
        { className: "text-center" },
        React.createElement(
          "div",
          { className: "flex justify-center mb-4 text-xl" },
          icon
        ),
        React.createElement(
          DialogTitle,
          { 
            className: "text-lg font-semibold",
            style: { color: "#1f2937" }
          },
          title
        ),
        message &&
          React.createElement(
            DialogDescription,
            { 
              className: "text-sm mt-2",
              style: { color: "#1f2937" }
            },
            message
          )
      ),
      React.createElement(
        DialogFooter,
        { className: "flex flex-col sm:flex-row gap-3 sm:justify-end" },
        showCancel &&
          React.createElement(
            Button,
            {
              variant: "outline",
              onClick: handleCancel,
              className: "w-full sm:w-auto order-1 sm:order-1",
            },
            cancelText
          ),
        React.createElement(
          Button,
          {
            onClick: handleConfirm,
            className: cn(
              "w-full sm:w-auto order-2 sm:order-2",
              colors.confirmButton
            ),
          },
          confirmText
        )
      )
    )
  )
}

export default DialogHandler
